import React, { ChangeEvent, FunctionComponent, MouseEvent, useEffect, useState } from 'react';

import { isEmpty, isEqual, keys, keyBy, map, partial, pickBy } from 'lodash';
import { Modal, Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { submitFeedback } from 'admin/inbox-config/InboxConfig';
import { FieldName as LabelField, Label } from 'admin/labels/Label.types';
import { getLabels } from 'admin/labels/Labels.api';
import { useProfile } from 'auth/Auth';
import { Sentiment } from 'common/comments2/Comments2.types';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { Overlay } from 'core/overlay/Overlay';
import { SearchRequest } from 'core/util/Search';
import { FormInputs, FormControl } from 'reviews2/feed/feedback-yeti/Feedback.types';

import 'reviews2/feed/feedback-yeti/Feedback.scss';

export interface Props {
    review: any;
    show: boolean;
    onClose: (e: MouseEvent | null) => void;
}

export const Feedback: FunctionComponent<Props> = ({ show, onClose: close, review }): JSX.Element => {
    const { t } = useTranslation('reviews2');
    const [{ profile }] = useProfile();
    const tenantId = profile?.tenant?.id;

    const { errors, register, handleSubmit, getValues } = useForm<FormInputs>();
    const [, dispatchMessage] = useMessage();

    const [, setFeedbackDesc] = useState<string>('');
    const [labelIndex, setLabelIndex] = useState<Record<string, Label>>();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [showCategoryError, setShowCategoryError] = useState<boolean>(false);

    useEffect(() => {
        if (!isEmpty(review.taxonomy_categories) && show) {
            (async (): Promise<void> => {
                if (!review.categoryLabels) {
                    const search: SearchRequest = {
                        filters: [
                            {
                                name: LabelField.Id,
                                values: map(map(review.taxonomy_categories, 'uuid'), String),
                            },
                        ],
                    };

                    const result: Label[] = await trackPromise(getLabels(-1, search), 'feedback');

                    setLabelIndex(keyBy(result, 'id'));
                } else {
                    setLabelIndex(keyBy(review.categoryLabels, 'id'));
                }
            })();
        }
    }, [tenantId, review, show]);

    const getCategorySentiment = (sentiment: number) => {
        switch (sentiment) {
            case Sentiment.Negative:
                return 'negative';
            case Sentiment.Neutral:
                return 'neutral';
            case Sentiment.Positive:
                return 'positive';
            default:
                return '';
        }
    };

    const validateCategorySelection = () => {
        const values = getValues();

        const { description: feedbackText, ...rest } = values;
        const categoryIDs = map(keys(pickBy(rest, partial(isEqual, true))), Number);

        setShowCategoryError(!categoryIDs?.length);
    };

    const onSubmit = async (formData: FormInputs) => {
        try {
            setSubmitting(true);

            const { description: feedbackText, ...rest } = formData;
            const categoryIDs = map(keys(pickBy(rest, partial(isEqual, true))), Number);

            if (!categoryIDs?.length) {
                return;
            }

            const jiraIssueKey = await trackPromise(
                submitFeedback(Number(tenantId), { commentID: review.document_id, feedbackText, categoryIDs }),
                'feedback',
            );

            // QA purpose
            console.log('Jira issue key:', jiraIssueKey);

            dispatchMessage({
                type: MessageActionType.ShowSuccess,
                text: t('FEEDBACK_SUBMIT_SUCCESS'),
            });

            close(null);
        } catch (err) {
            dispatchMessage({
                type: MessageActionType.ShowError,
                text: t('FEEDBACK_SUBMIT_FAILURE'),
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={close}
            onClick={(e: Event) => e.stopPropagation()}
            className="r4e-review-feedback-yeti"
            data-testid="r4e-review-feedback"
        >
            <Overlay area="feedback" />
            <Modal.Header closeButton>
                <Modal.Title>{t('PROVIDE_FEEDBACK')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form data-testid="feedback-form" noValidate onSubmit={handleSubmit(onSubmit)}>
                    <Form.Text as="p" className="question">
                        {t('WHICH_ONE_PROVIDE_FEEDBACK')}
                    </Form.Text>
                    <Form.Group>
                        <Form.Label>{t('CATEGORIES')}</Form.Label>
                        {review.taxonomy_categories?.map((category: any) => {
                            if (!labelIndex?.[category.uuid]?.displayName) {
                                return null;
                            }

                            return (
                                <Form.Check
                                    key={`${category.uuid}_${category.sentiment}`}
                                    name={String(category.uuid)}
                                    type="checkbox"
                                    label={
                                        <div
                                            className={`r4e-review-category ${getCategorySentiment(
                                                category.sentiment,
                                            )}`}
                                        >
                                            {labelIndex?.[category.uuid]?.displayName}
                                        </div>
                                    }
                                    ref={register({ required: false })}
                                />
                            );
                        })}
                        {showCategoryError && (
                            <Form.Control.Feedback className="d-block" type="invalid">
                                {t('VALIDATION_CATEGORY_REQUIRED')}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>{t('FEEDBACK_DESCRIPTION')}</Form.Label>
                        <Form.Control
                            name={FormControl.Description}
                            as="textarea"
                            placeholder={t('ADD_DESCRIPTION')}
                            ref={register({ required: true, pattern: /^(?!\s*$).+/ })}
                            rows={3}
                            isInvalid={!!errors[FormControl.Description]}
                            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                                setFeedbackDesc(event.target.value);
                            }}
                        />
                        {errors[FormControl.Description] && (
                            <Form.Control.Feedback className="d-block" type="invalid">
                                {t('VALIDATION_FEEDBACK_REQUIRED')}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                    <div className="actions-container">
                        <Button disabled={submitting} variant="secondary" onClick={close}>
                            {t('CANCEL')}
                        </Button>
                        <Button
                            disabled={submitting}
                            variant="primary"
                            type="submit"
                            onClick={validateCategorySelection}
                        >
                            {t('SUBMIT')}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};
